// ファイル取込フロー（キュー処理・CRS 不明時のモーダル退避・リトライ）。
//
// 役割: App.jsx から切り出した取込結線フック。複数ファイルを順に取り込み、CRS 不明データは
//       pendingImport に退避して中断、ユーザー確認後に宣言 CRS で再実行する。取込成功時は
//       注入された addLayerToMap で地図へ反映し、任意の onImported(layer) で後続処理を App 側へ委ねる。
// 注入: { addLayerToMap, log, onImported? }。
import { useCallback, useState } from 'react'

import { importFile } from '../data/layer-service'
import { UnknownCrsError } from '../duckdb/geo-normalize'

export function useImportFlow({ addLayerToMap, log, onImported }) {
  // --- 取込状態と CRS 確認モーダル ---
  const [busy, setBusy] = useState(false)
  const [pendingImport, setPendingImport] = useState(null) // { file, queue }

  // ファイル取込（CRS 不明時はモーダルへ退避して中断）。
  const processImport = useCallback(
    async (file, queue, declaredCrs) => {
      setBusy(true)
      try {
        const layer = await importFile(file, { declaredCrs })
        addLayerToMap(layer)
        log(`取込: ${layer.displayName}（${layer.featureCount} 件）`)
        // 取込後の追加処理（SQL タブの自動サンプル等）は App 側へ委ねる。
        onImported?.(layer)
        if (queue.length > 0) {
          const [next, ...rest] = queue
          await processImport(next, rest)
        }
      } catch (e) {
        if (e instanceof UnknownCrsError) {
          setPendingImport({ file, queue })
        } else {
          log(`エラー: ${String(e?.message ?? e)}`)
        }
      } finally {
        setBusy(false)
      }
    },
    [addLayerToMap, log, onImported],
  )

  const handleFiles = useCallback(
    (files) => {
      if (files.length === 0) return
      const [first, ...rest] = files
      processImport(first, rest)
    },
    [processImport],
  )

  const handleCrsConfirm = useCallback(
    (crs) => {
      const pending = pendingImport
      setPendingImport(null)
      if (pending) processImport(pending.file, pending.queue, crs)
    },
    [pendingImport, processImport],
  )

  const handleCrsCancel = useCallback(() => {
    const pending = pendingImport
    setPendingImport(null)
    log(`取込中止: ${pending?.file?.name ?? ''}`)
    if (pending && pending.queue.length > 0) {
      const [next, ...rest] = pending.queue
      processImport(next, rest)
    }
  }, [pendingImport, processImport, log])

  return { busy, pendingImport, handleFiles, handleCrsConfirm, handleCrsCancel }
}
